import React, { useMemo } from "react";
import { yearHistogram, shipYear } from "../helpers/shipFilters";

// Bottom-of-map timeline: a histogram of wrecks by year of loss with a two-handle range slider.
export default function TimelineBar({ ships, bounds, from, to, includeUndated, shifted, onRange, onToggleUndated, onClose, onClear }) {
  const bars = useMemo(() => yearHistogram(ships, bounds), [ships, bounds]);
  const max = useMemo(() => Math.max(1, ...bars.map((b) => b.count)), [bars]);
  const undated = useMemo(() => ships.filter((s) => shipYear(s) === null).length, [ships]);

  if (!bounds) {
    return (
      <div className={`timeline-bar${shifted ? " shifted" : ""}`} role="group" aria-label="Timeline">
        <div className="timeline-head"><strong>Timeline</strong><button type="button" className="filter-close" onClick={onClose} aria-label="Close timeline">×</button></div>
        <p className="filter-note">Not enough wrecks have a date yet to draw a timeline. Add the date of loss when you submit or edit a wreck.</p>
      </div>
    );
  }

  const lo = from ?? bounds.min;
  const hi = to ?? bounds.max;
  const inRange = (b) => b.to >= lo && b.from <= hi;
  const inCount = ships.filter((s) => { const y = shipYear(s); return y !== null && y >= lo && y <= hi; }).length;
  const pct = (y) => ((y - bounds.min) / (bounds.max - bounds.min)) * 100;
  const active = from !== null || to !== null;

  // keep the handles from crossing
  const setLo = (v) => onRange(Math.min(Number(v), hi), hi);
  const setHi = (v) => onRange(lo, Math.max(Number(v), lo));

  return (
    <div className={`timeline-bar${shifted ? " shifted" : ""}`} role="group" aria-label="Timeline by year of loss">
      <div className="timeline-head">
        <strong>Year of loss</strong>
        <span className="timeline-range">{lo} – {hi}<em> · {inCount} wreck{inCount === 1 ? "" : "s"}</em></span>
        <div className="timeline-actions">
          {active && <button type="button" className="filter-reset" onClick={onClear}>Show all years</button>}
          <button type="button" className="filter-close" onClick={onClose} aria-label="Close timeline">×</button>
        </div>
      </div>

      <div className="timeline-chart">
        <div className="timeline-bars" aria-hidden="true">
          {bars.map((b) => (
            <div key={b.from} className={`timeline-bar-col${inRange(b) ? " in" : ""}`} title={`${b.from}${b.to > b.from ? `–${b.to}` : ""}: ${b.count}`}>
              <i style={{ height: `${b.count ? Math.max(8, (b.count / max) * 100) : 0}%` }} />
            </div>
          ))}
        </div>
        <div className="timeline-slider">
          <div className="timeline-track"><div className="timeline-fill" style={{ left: `${pct(lo)}%`, width: `${pct(hi) - pct(lo)}%` }} /></div>
          <input type="range" min={bounds.min} max={bounds.max} step={1} value={lo} onChange={(e) => setLo(e.target.value)} aria-label="Earliest year" />
          <input type="range" min={bounds.min} max={bounds.max} step={1} value={hi} onChange={(e) => setHi(e.target.value)} aria-label="Latest year" />
        </div>
        <div className="timeline-axis"><span>{bounds.min}</span><span>{Math.round((bounds.min + bounds.max) / 2)}</span><span>{bounds.max}</span></div>
      </div>

      {undated > 0 && (
        <label className="filter-check timeline-undated">
          <input type="checkbox" checked={includeUndated} onChange={(e) => onToggleUndated(e.target.checked)} />
          {active ? `Include ${undated} wreck${undated === 1 ? "" : "s"} with no date` : `${undated} wreck${undated === 1 ? " has" : "s have"} no date and are hidden while a range is set`}
        </label>
      )}
    </div>
  );
}
